import { useCallback, useEffect, useRef } from "react";
import { useSetTokens, useTokensValues } from "../tokens";
import type { Tokens } from "../../store";
import {
  enqueueRequest,
  initializeTokens,
  updateCurrentTokens,
} from "./tokenQueue";

/**
 * Hook that provides queue access and keeps module-level tokens in sync with Jotai.
 *
 * The queue maintains its own token state (for passing to callbacks), but we
 * also sync with Jotai so the rest of the app sees updated tokens.
 *
 * Why two token states?
 * - Queue's `currentTokens`: Used internally to pass to callbacks. Updated synchronously.
 * - Jotai `tokensAtom`: Used by the rest of the app. Synced after each request.
 *
 * This dual state is necessary because Jotai updates are async and we need
 * guaranteed sequential token passing.
 */
export const useTokenQueue = () => {
  const tokens = useTokensValues();
  const setTokens = useSetTokens();
  const hasInitialized = useRef(false);

  // Initialize queue tokens once when they become available (non-empty)
  useEffect(() => {
    if (
      !hasInitialized.current &&
      tokens.authenticityToken &&
      tokens.journeyToken
    ) {
      initializeTokens(tokens);
      hasInitialized.current = true;
    }
  }, [tokens]); // Watch tokens - initialize when they become available

  /**
   * Enqueue a request that needs token access.
   *
   * Usage:
   * ```
   * const result = await enqueue(async (tokens) => {
   *   const response = await apiRequest(url, { ...tokens, ...data });
   *   return {
   *     newTokens: response.data.configuration,
   *     result: response.data
   *   };
   * });
   * ```
   *
   * The queue ensures:
   * 1. Requests are processed sequentially
   * 2. Each request receives the tokens from the previous response
   * 3. Your promise resolves with your specific result
   */
  const enqueue = useCallback(
    <T>(
      execute: (tokens: Tokens) => Promise<{ newTokens: Tokens; result: T }>,
    ): Promise<T> => {
      return enqueueRequest(async currentTokens => {
        const { newTokens, result } = await execute(currentTokens);

        // Sync rotated tokens to Jotai atom (for rest of app)
        setTokens(newTokens);

        // Also update queue's internal token state (for next queued request)
        updateCurrentTokens(newTokens);

        return { newTokens, result };
      });
    },
    [setTokens],
  );

  return { enqueue };
};
